import { ApplicationElement, html } from '@effectivastudio/lit-components';
import translations from './translations.js';
import style from './style.lit.css';

import '@effectivastudio/lit-components/components/timeago/element.js';
import '@effectivastudio/lit-components/components/icon/element.js';
import '@effectivastudio/lit-components/components/icon_block/element.js';
import '@effectivastudio/lit-components/components/grid_list/element.js';

/**
 *
 * @prop cardType - (options: news, discussion, event, poll, date, organize, marketplace, booking)
 * @prop unitType - (options: open, close, private)
 * @prop streamHref - Link to origin (e.g. forum, marketplace, booking)
 */
export class CardHeader extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];
  /** @internal */
  static translations = translations;

  static properties = {
    createdAt: { type: Date },
    streamHref: { type: String },
    internal: { type: Boolean },
    isOfficial: { type: Boolean },
    userName: { type: String },
    userHref: { type: String },
    unitTitle: { type: String },
    unitType: { type: String },
    unitHref: { type: String },
    isUnitGroup: { type: Boolean },
    cardType: { type: String },
    isSmartCollapsed: { type: Boolean },
    isGroupAnnouncement: { type: Boolean },
    isFullView: { type: Boolean },

    currentUserIsConfirmed: { type: Boolean, state: true, attribute: false },
  };

  get currentUserIsConfirmed() {
    return this._currentUserIsConfirmed || this.AppConfig?.user?.isConfirmed;
  }

  set currentUserIsConfirmed(value) {
    this._currentUserIsConfirmed = value;
  }

  requirePhoneConfirmation() {
    if (confirm('Are you phone confirmed?')) {
      alert('Great!');
      this.currentUserIsConfirmed = true;
    }
  }

  /** @internal */
  get originCollection() {
    const collection = {};

    if (this.cardType === 'booking') {
      collection.name = this.t('section.booking');
      collection.icon = 'calendar-check';
    } else if (this.cardType === 'marketplace') {
      collection.name = this.t('section.marketplace');
      collection.icon = 'shopping-basket';
    } else {
      collection.name = this.t('section.internal');
      collection.icon = 'comments-alt';
    }

    return collection;
  }

  handleUserClick(e) {
    if (!this.currentUserIsConfirmed) {
      e.preventDefault();
      this.requirePhoneConfirmation();
    }
  }

  renderUserLink() {
    return html`
      <a href=${this.userHref} @click=${this.handleUserClick}>
        ${this.userName}
      </a>
    `;
  }

  renderUnitLink() {
    return html` <a href=${this.unitHref}>${this.unitTitle}</a> `;
  }

  renderOfficialIcon() {
    return html` <ef-icon name="shield-check"></ef-icon> `;
  }

  renderOrigin() {
    return html`
      <a href=${this.streamHref} class="origin" @click=${this.handleClick}>
        <ef-icon-block
          icon=${this.originCollection.icon}
          text=${this.originCollection.name}></ef-icon-block>
      </a>
    `;
  }

  renderContentWitUserInfo() {
    return html`
      <span>${this.t('publish_details.by_user')}</span>
      ${this.userHref
        ? this.renderUserLink()
        : html`<span>${this.userName}</span>`}
      ${this.isOfficial ? this.renderOfficialIcon() : ''}
      ${this.isFullView
        ? html`
            <span>
              ${this.t(
                `publish_details.in_${this.isUnitGroup ? 'group' : 'community'}`
              )}
            </span>
            ${this.renderUnitLink()}
          `
        : ''}
    `;
  }

  renderContentWithoutUserInfo() {
    return html`
      <span>
        ${this.t(
          `publish_details.by_${this.isUnitGroup ? 'group' : 'community'}`
        )}
      </span>
      ${this.renderUnitLink()}
    `;
  }

  renderDetails() {
    if (
      !this.isGroupAnnouncement &&
      (this.internal || this.unitType === 'private')
    ) {
      return this.renderContentWitUserInfo();
    }

    return this.renderContentWithoutUserInfo();
  }

  render() {
    return html`
      <slot name="avatar"></slot>
      <div class="info">
        <slot name="title"></slot>
        <ef-grid-list
          col="0"
          gap="small"
          alignH="center"
          class="publish-details">
          ${this.renderDetails()}
          <ef-timeago timestamp="${this.createdAt}"></ef-timeago>
        </ef-grid-list>
        ${!this.isSmartCollapsed && this.streamHref ? this.renderOrigin() : ''}
      </div>
    `;
  }
}

export default CardHeader;

CardHeader.registerTagName('bu-card-header');

customElements.get('bu-card-header') ||
  customElements.define('bu-card-header', CardHeader);
